import React, { useState, useEffect, useRef } from 'react';
import { ModalX } from '../../../components';
import { Button, Input, IconButton, Tooltip } from '@material-tailwind/react';
import { useAddTeamMember, useAddUser, useUpdateUser } from '../../../custom-hooks/use-users';
import { NotifyError } from '../../../utils/functions';
import { useCountries } from "use-react-countries";
import { ChevronDownIcon, InfoIcon } from "lucide-react";
import { phoneNumberInputInstructionsTemplate } from "../../../utils/templates";

// Functional component
const CustomerFormModal = ({
    isOpen,
    onClose,
    isUpdating,
    updatingUserId,
    defaultData,
    callback = () => { }
}) => {
    // * ----------------------------------------------------------------------------------------------------- Mutations
    const addUserMutation = useAddUser();
    const updateUserMutation = useUpdateUser();
    const addTeamMemberMutation = useAddTeamMember();

    // * ----------------------------------------------------------------------------------------------------- State variables
    const [formData, setFormData] = useState(defaultData || {});
    
    const { countries } = useCountries();
    const [countryIndex, setCountryIndex] = useState(
        countries.findIndex(c => c.countryCallingCode === "+92")
    );
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchCountry, setSearchCountry] = useState("");
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Initial generated password on Add and Phone number extraction
    useEffect(() => {
        if (isOpen) {
            let initialData = { ...(defaultData || {}) };
            
            // Password gen
            if (!isUpdating && !initialData.password) {
                initialData.password = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
            }

            // Phone extraction
            if (initialData.phone) {
                let num = initialData.phone;
                if (num.startsWith('0')) {
                    num = '+92' + num.substring(1);
                } else if (!num.startsWith('+')) {
                    num = '+' + num;
                }
                const sortedCountries = [...countries].sort((a, b) => b.countryCallingCode.length - a.countryCallingCode.length);
                const matchedCountry = sortedCountries.find(c => num.startsWith(c.countryCallingCode));
                if (matchedCountry) {
                    const cIdx = countries.findIndex(c => c.name === matchedCountry.name);
                    if (cIdx !== -1) setCountryIndex(cIdx);
                    initialData.phoneNumber = num.substring(matchedCountry.countryCallingCode.length);
                } else {
                    initialData.phoneNumber = initialData.phone;
                }
            } else {
                // If it's a new customer without a phone number, reset country to default
                setCountryIndex(countries.findIndex(c => c.countryCallingCode === "+92"));
                initialData.phoneNumber = '';
            }

            setFormData(initialData);
        }
    }, [isOpen]);

    const { name: countryName, flags, countryCallingCode } = countries[countryIndex] || countries[0] || {};

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
                setSearchCountry("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (dropdownOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [dropdownOpen]);

    // * ----------------------------------------------------------------------------------------------------- Handlers
    // Handle form input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Handle form submit
    const handleFormSubmit = async (e) => {
        e.preventDefault();

        let finalPhone = "";
        if (!formData.teamId && formData.phoneNumber) {
            let num = formData.phoneNumber;
            if (num.startsWith('0')) {
                num = num.substring(1);
            }

            finalPhone = countryCallingCode + num;

            if (countryCallingCode === '+92' && num.length !== 10) {
                NotifyError("For Pakistan (+92), the phone number must be 10 digits");
                return;
            }

            if (!/^\+\d{7,15}$/.test(finalPhone)) {
                NotifyError("Please enter a valid phone number containing only numeric digits (7-15 digits globally)");
                return;
            }
        }

        // Prepare the payload
        const payload = { ...formData };
        if (!formData.teamId) {
            payload.phone = finalPhone;
        }

        try {
            if (isUpdating) {
                await updateUserMutation.mutateAsync({ userId: updatingUserId, data: payload });

            } else {
                if (payload.teamId) {
                    await addTeamMemberMutation.mutateAsync(payload);
                }
                else {
                    // Update user logic
                    await addUserMutation.mutateAsync(payload);
                }
            }
            onClose(false);
            setFormData(defaultData || {});
        } catch (error) {
            console.error(error);
            NotifyError('Failed to save customer', error);
        }
        finally {
            callback();
        }
    };

    // * ----------------------------------------------------------------------------------------------------- Render
    return (
        <ModalX
            isOpen={isOpen}
            onClose={() => onClose(false)}
            title={isUpdating ? "Update Customer" : "Add Customer"}
            maxWidth="md"
        >
            <form onSubmit={handleFormSubmit}>
                <div className="space-y-4">
                    <Input
                        label="Full Name"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleInputChange}
                        required
                    />
                    <Input
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email || ""}
                        onChange={handleInputChange}
                        required
                    />
                    <Input
                        label="CNIC"
                        name="cnic"
                        value={formData.cnic || ""}
                        onChange={handleInputChange}
                        required
                    />
                    {
                        !isUpdating && !formData.teamId && (
                            <Input
                                label="Password"
                                name="password"
                                value={formData.password || ""}
                                onChange={handleInputChange}
                                required
                            />
                        )
                    }
                    {
                        !formData.teamId && (
                            <div className="relative flex w-full">
                                <div className="relative" ref={dropdownRef}>
                                    <button
                                        type="button"
                                        onClick={() => setDropdownOpen(!dropdownOpen)}
                                        className="flex h-10 items-center gap-2 rounded-l-md border border-r-0 border-blue-gray-200 bg-blue-gray-500/10 pl-3 pr-2 text-sm text-blue-gray-700 hover:bg-blue-gray-500/20 transition-colors"
                                    >
                                        {flags?.svg && (
                                            <img
                                                src={flags.svg}
                                                alt={countryName}
                                                className="h-4 w-4 rounded-full object-cover"
                                            />
                                        )}
                                        {countryCallingCode}
                                        <ChevronDownIcon size={14} className={`transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {dropdownOpen && (
                                        <div className="absolute top-full left-0 mt-1 w-[18rem] max-h-[20rem] bg-white border border-blue-gray-100 rounded-lg shadow-lg z-[10000] flex flex-col">
                                            <div className="p-2 border-b border-blue-gray-100">
                                                <input
                                                    ref={searchInputRef}
                                                    type="text"
                                                    placeholder="Search country..."
                                                    value={searchCountry}
                                                    onChange={(e) => setSearchCountry(e.target.value)}
                                                    className="w-full px-3 py-1.5 text-sm border border-blue-gray-200 rounded-md outline-none focus:border-gray-900 bg-transparent placeholder:text-blue-gray-300"
                                                />
                                            </div>
                                            <div className="overflow-y-auto flex-1">
                                                {countries
                                                    .map((c, idx) => ({ ...c, originalIndex: idx }))
                                                    .filter(({ name, countryCallingCode }) =>
                                                        name.toLowerCase().includes(searchCountry.toLowerCase()) ||
                                                        countryCallingCode.includes(searchCountry)
                                                    )
                                                    .map(({ name, flags: cFlags, countryCallingCode: cCallingCode, originalIndex }) => (
                                                        <div
                                                            key={name}
                                                            className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-blue-gray-50 transition-colors"
                                                            onClick={() => {
                                                                setCountryIndex(originalIndex);
                                                                setDropdownOpen(false);
                                                                setSearchCountry("");
                                                            }}
                                                        >
                                                            <img
                                                                src={cFlags.svg}
                                                                alt={name}
                                                                className="h-5 w-5 rounded-full object-cover"
                                                            />
                                                            {name} <span className="ml-auto text-blue-gray-400">{cCallingCode}</span>
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <Input
                                    type="tel"
                                    placeholder="Mobile Number *"
                                    className="rounded-l-none !border-t-blue-gray-200 focus:!border-t-gray-900 placeholder:opacity-100"
                                    required={!formData.teamId}
                                    name="phoneNumber"
                                    value={formData.phoneNumber || ""}
                                    onChange={handleInputChange}
                                    labelProps={{ className: "hidden" }}
                                    containerProps={{ className: "min-w-0" }}
                                    icon={<Tooltip content={phoneNumberInputInstructionsTemplate} placement="top" className="z-[10000]">
                                        <IconButton className="rounded-full h-4 w-4" size="sm">
                                            <InfoIcon size={14} />
                                        </IconButton>
                                    </Tooltip>}
                                />
                            </div>
                        )
                    }
                </div>
                <div className="mt-4 flex justify-end space-x-2">
                    <Button
                        onClick={() => onClose(false)}
                        className="rounded-full"
                        variant="text"
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={updateUserMutation.isLoading || addUserMutation.isLoading}
                        loading={updateUserMutation.isLoading || addUserMutation.isLoading}
                        type="submit"
                        className="rounded-full flex gap-2"
                        color={isUpdating ? "blue" : "green"}
                    >
                        {isUpdating ? "Update" : "Add"}
                    </Button>
                </div>
            </form>
        </ModalX>
    );
};

export default CustomerFormModal;